import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { cadenceMs, freshness, type Point, type Sex } from "@heraion/shared";

import { useChartReadout } from "../../charts/pointSelection";
import { WagmiLineChart, type ChartSeries } from "../../charts/WagmiLineChart";
import { StaleBadge } from "../../primitives/StaleBadge";
import type { UiTheme } from "../../theme";

// One chart of MeasurementTrends, split out so each chart owns its own
// selection (PG-64 fetta 3): the header quotes the tapped point, «value ·
// date», the latest by default.

export type MeasurementSeries = {
  key: string;
  title: string;
  points: Point[];
  /** Measured curve imported via Health Connect, drawn dashed alongside the JL
   *  estimate (dual-curve, labelled by provenance, slice 2d). */
  secondaryPoints?: Point[];
  yFormat: (n: number) => string;
};

type Props = {
  series: MeasurementSeries;
  sex: Sex | undefined;
  locale: string;
  theme: UiTheme;
  nowMs: number;
};

export function MeasurementTrendChart({ series: s, sex, locale, theme, nowMs }: Props) {
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { selection, readout, readoutProps } = useChartReadout(s.points, s.yFormat, true);
  const chartSeries: ChartSeries[] = [
    {
      key: s.key,
      points: s.points,
      variant: "primary",
      headline: true,
      // PG-53: same word as the legend below ("Solid: estimate").
      name: t("mobile.body.estimateSeriesName", { defaultValue: "Estimate" }),
    },
  ];
  if (s.secondaryPoints)
    chartSeries.push({
      key: `${s.key}-hc`,
      points: s.secondaryPoints,
      variant: "secondary",
      // PG-53: reuses the existing standalone name, not a third string.
      name: t("mobile.energySource.healthConnect", { defaultValue: "Health Connect" }),
    });
  // The JL estimate (primary) is what the header reports, so it is the
  // series whose silence matters and the one a tap reads (`headline`).
  const xs = s.points.map((p) => p.x);
  const fresh = freshness(xs, nowMs);
  const lastAt = xs[xs.length - 1];
  return (
    <View style={styles.chartBlock}>
      <View style={styles.chartHead}>
        <Text style={styles.chartTitle}>{s.title}</Text>
        <Text style={styles.chartCurrent} {...readoutProps}>
          {readout}
        </Text>
      </View>
      {fresh.known && fresh.stale && lastAt !== undefined ? (
        <View style={styles.ageRow}>
          <StaleBadge theme={theme} atMs={lastAt} locale={locale} cadenceMs={cadenceMs(xs)} />
        </View>
      ) : null}
      <WagmiLineChart
        series={chartSeries}
        yFormat={s.yFormat}
        theme={theme}
        nowMs={nowMs}
        selection={selection}
      />
      {s.secondaryPoints ? (
        <Text style={styles.legend}>
          {t("mobile.body.dualCurveLegend", {
            defaultValue: "Solid: estimate · Dashed: Health Connect",
          })}
        </Text>
      ) : null}
      {/* MS-2: body fat is a tape measurement, not a scale reading, it moves
          with waist/neck (and hips for women), not weight. A plain factual
          line, gender-aware from the sex already in scope. */}
      {s.key === "fat" ? (
        <Text style={styles.provenance}>
          {sex === "female"
            ? t("mobile.body.bodyFatProvenanceWithHips", {
                defaultValue: "Body fat is calculated from waist, neck and hips.",
              })
            : t("mobile.body.bodyFatProvenance", {
                defaultValue: "Body fat is calculated from waist and neck.",
              })}
        </Text>
      ) : null}
    </View>
  );
}

/** The series title style, shared with MeasurementEmptyRow. */
export const measurementTitleStyle = (theme: UiTheme) =>
  ({
    color: theme.palette.ink2,
    fontSize: theme.typography.sizes.sm,
    fontWeight: "700",
  }) as const;

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    chartBlock: { gap: s.s1, marginTop: s.s3 },
    ageRow: { flexDirection: "row" },
    chartHead: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
    },
    chartTitle: measurementTitleStyle(theme),
    chartCurrent: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    legend: { color: p.ink3, fontSize: ty.sizes.xs, marginTop: 2 },
    // MS-2 provenance line: explanatory content (not a decorative legend), so
    // a step up from the xs legend to sm.
    provenance: { color: p.ink3, fontSize: ty.sizes.sm, marginTop: s.s1 },
  });
}
